import { api } from "@/lib/apiClient";
import {
  paymentMethodMeta,
  paymentMethodUsesBank,
  type ExpensePaymentMethod,
} from "@/lib/expenses/expensesStore";
import { isAxiosError } from "axios";

export type LoanStatus = "active" | "completed";

export type EmployeeLoanRecord = {
  id: string;
  employeeId: string;
  employeeName: string;
  loanAmount: string;
  installmentAmount: string;
  totalInstallments: string;
  remainingAmount: string;
  loanDate: string;
  startDate: string;
  paymentMethod: ExpensePaymentMethod | "";
  paymentMethodLabel: string;
  bankId: string;
  bankName: string;
  referenceNo: string;
  chequeNo: string;
  chequeDate: string;
  status: LoanStatus;
  hasPaidInstallment: boolean;
  createdAt: string;
  updatedAt: string;
};

export type EmployeeLoanInstallmentRecord = {
  id: string;
  loanId: string;
  salaryId: string | null;
  periodYear: string;
  periodMonth: string;
  installmentAmount: string;
  deductedOn: string | null;
  status: "paid" | "pending" | "unpaid";
  note: string | null;
  isOutsideRepayment: boolean;
  createdAt: string;
  updatedAt: string;
};

type PaginatedResponse = {
  data?: Record<string, unknown>[];
  meta?: { current_page?: number; last_page?: number; per_page?: number; total?: number };
};

function str(v: unknown): string {
  if (v == null || v === "") return "";
  return String(v);
}

function mapPaymentMethod(v: unknown): ExpensePaymentMethod | "" {
  if (v === "bank") return "online";
  if (v === "cash" || v === "online" || v === "cheque" || v === "pay_order" || v === "cash_deposit") return v;
  return "";
}

function mapLoan(raw: Record<string, unknown>): EmployeeLoanRecord {
  const status = str(raw.status) === "completed" ? "completed" : "active";
  const paymentMethod = mapPaymentMethod(raw.payment_method);
  const bank = raw.bank && typeof raw.bank === "object" ? (raw.bank as Record<string, unknown>) : null;
  return {
    id: str(raw.id),
    employeeId: str(raw.employee_id),
    employeeName: str(raw.employee_name),
    loanAmount: str(raw.loan_amount),
    installmentAmount: str(raw.installment_amount),
    totalInstallments: str(raw.total_installments),
    remainingAmount: str(raw.remaining_amount),
    loanDate: str(raw.loan_date || raw.start_date).slice(0, 10),
    startDate: str(raw.start_date).slice(0, 10),
    paymentMethod,
    paymentMethodLabel: str(raw.payment_method_label) || paymentMethodMeta(paymentMethod)?.label || "",
    bankId: raw.bank_id != null ? str(raw.bank_id) : "",
    bankName: bank?.name != null ? str(bank.name) : "",
    referenceNo: raw.reference_no != null ? str(raw.reference_no) : "",
    chequeNo: raw.cheque_no != null ? str(raw.cheque_no) : "",
    chequeDate: raw.cheque_date != null ? str(raw.cheque_date).slice(0, 10) : "",
    status,
    hasPaidInstallment: Boolean(raw.has_paid_installment),
    createdAt: str(raw.created_at),
    updatedAt: str(raw.updated_at),
  };
}

function mapInstallment(raw: Record<string, unknown>): EmployeeLoanInstallmentRecord {
  const rawStatus = str(raw.status);
  const status: EmployeeLoanInstallmentRecord["status"] =
    rawStatus === "unpaid" ? "unpaid" : rawStatus === "pending" ? "pending" : "paid";
  const amount = Number(str(raw.installment_amount).replace(/,/g, ""));
  return {
    id: str(raw.id),
    loanId: str(raw.loan_id),
    salaryId: raw.salary_id != null ? str(raw.salary_id) : null,
    periodYear: str(raw.period_year),
    periodMonth: str(raw.period_month),
    installmentAmount: str(raw.installment_amount),
    deductedOn: raw.deducted_on != null ? str(raw.deducted_on).slice(0, 10) : null,
    status,
    note: raw.note != null ? str(raw.note) : null,
    isOutsideRepayment:
      Boolean(raw.is_outside_repayment) || (raw.salary_id == null && status === "paid" && Number.isFinite(amount) && amount > 0),
    createdAt: str(raw.created_at),
    updatedAt: str(raw.updated_at),
  };
}

export function loansApiErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const data = error.response?.data as { message?: string; errors?: Record<string, string[]> } | undefined;
    if (data?.errors) {
      const first = Object.values(data.errors).flat()[0];
      if (first) return first;
    }
    if (typeof data?.message === "string") return data.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return "Something went wrong.";
}

export async function listEmployeeLoans(params: {
  employeeId: string;
  page?: number;
  perPage?: number;
  status?: "all" | LoanStatus;
}): Promise<{ rows: EmployeeLoanRecord[]; meta: { currentPage: number; lastPage: number; perPage: number; total: number } }> {
  const response = await api.get<PaginatedResponse>(`/api/v1/employees/${encodeURIComponent(params.employeeId)}/loans`, {
    params: {
      page: params.page,
      per_page: params.perPage,
      status: params.status && params.status !== "all" ? params.status : undefined,
    },
  });
  const list = Array.isArray(response.data?.data) ? response.data.data : [];
  const meta = response.data?.meta ?? {};
  return {
    rows: list.map((r) => mapLoan(r as Record<string, unknown>)),
    meta: {
      currentPage: meta.current_page ?? 1,
      lastPage: meta.last_page ?? 1,
      perPage: meta.per_page ?? params.perPage ?? 20,
      total: meta.total ?? list.length,
    },
  };
}

export async function listAllEmployeeLoans(params: {
  page?: number;
  perPage?: number;
  status?: "all" | LoanStatus;
  employeeId?: string;
}): Promise<{ rows: EmployeeLoanRecord[]; meta: { currentPage: number; lastPage: number; perPage: number; total: number } }> {
  const response = await api.get<PaginatedResponse>("/api/v1/loans", {
    params: {
      page: params.page,
      per_page: params.perPage,
      status: params.status && params.status !== "all" ? params.status : undefined,
      employee_id: params.employeeId || undefined,
    },
  });
  const list = Array.isArray(response.data?.data) ? response.data.data : [];
  const meta = response.data?.meta ?? {};
  return {
    rows: list.map((r) => mapLoan(r as Record<string, unknown>)),
    meta: {
      currentPage: meta.current_page ?? 1,
      lastPage: meta.last_page ?? 1,
      perPage: meta.per_page ?? params.perPage ?? 20,
      total: meta.total ?? list.length,
    },
  };
}

export function validateLoanPayment(params: {
  paymentMethod: ExpensePaymentMethod | "";
  bankId: string;
  referenceNo: string;
  chequeNo: string;
}): string | null {
  if (!params.paymentMethod) return "Select a payment method.";
  if (paymentMethodUsesBank(params.paymentMethod) && !params.bankId) {
    return "Bank is required for this payment method.";
  }
  if (params.paymentMethod === "cheque" && !params.chequeNo.trim()) {
    return "Cheque # is required.";
  }
  const meta = paymentMethodMeta(params.paymentMethod);
  if (meta?.referenceLabel && params.paymentMethod !== "cheque" && !params.referenceNo.trim()) {
    return `${meta.referenceLabel} is required.`;
  }
  return null;
}

function paymentPayload(params: {
  paymentMethod: ExpensePaymentMethod | "";
  bankId: string;
  referenceNo: string;
  chequeNo: string;
  chequeDate: string;
}): Record<string, unknown> {
  const usesBank = paymentMethodUsesBank(params.paymentMethod);
  return {
    payment_method: params.paymentMethod || null,
    bank_id: usesBank && params.bankId ? Number(params.bankId) : null,
    reference_no: params.paymentMethod !== "cheque" && params.paymentMethod !== "cash" ? params.referenceNo.trim() || null : null,
    cheque_no: params.paymentMethod === "cheque" ? params.chequeNo.trim() || null : null,
    cheque_date: params.paymentMethod === "cheque" ? params.chequeDate.trim() || null : null,
  };
}

export async function createEmployeeLoan(params: {
  employeeId: string;
  loanAmount: number | string;
  installmentAmount: number | string;
  loanDate: string;
  startDate: string;
  paymentMethod: ExpensePaymentMethod | "";
  bankId: string;
  referenceNo: string;
  chequeNo: string;
  chequeDate: string;
}): Promise<{ record: EmployeeLoanRecord; message: string }> {
  const response = await api.post<{ message?: string; data?: Record<string, unknown> }>(
    `/api/v1/employees/${encodeURIComponent(params.employeeId)}/loans`,
    {
      loan_amount: params.loanAmount,
      installment_amount: params.installmentAmount,
      loan_date: params.loanDate,
      start_date: params.startDate,
      ...paymentPayload(params),
    },
  );
  return { record: mapLoan((response.data?.data ?? {}) as Record<string, unknown>), message: str(response.data?.message) || "Created." };
}

export async function updateEmployeeLoan(params: {
  employeeId: string;
  id: string;
  patch: Partial<{
    installmentAmount: number | string;
    loanDate: string;
    startDate: string;
    status: LoanStatus;
    paymentMethod: ExpensePaymentMethod | "";
    bankId: string;
    referenceNo: string;
    chequeNo: string;
    chequeDate: string;
  }>;
}): Promise<{ record: EmployeeLoanRecord; message: string }> {
  const body: Record<string, unknown> = {};
  if (params.patch.installmentAmount !== undefined) body.installment_amount = params.patch.installmentAmount;
  if (params.patch.loanDate !== undefined) body.loan_date = params.patch.loanDate;
  if (params.patch.startDate !== undefined) body.start_date = params.patch.startDate;
  if (params.patch.status !== undefined) body.status = params.patch.status;
  if (params.patch.paymentMethod !== undefined) {
    Object.assign(
      body,
      paymentPayload({
        paymentMethod: params.patch.paymentMethod,
        bankId: params.patch.bankId ?? "",
        referenceNo: params.patch.referenceNo ?? "",
        chequeNo: params.patch.chequeNo ?? "",
        chequeDate: params.patch.chequeDate ?? "",
      }),
    );
  }

  const response = await api.patch<{ message?: string; data?: Record<string, unknown> }>(
    `/api/v1/employees/${encodeURIComponent(params.employeeId)}/loans/${encodeURIComponent(params.id)}`,
    body,
  );
  return { record: mapLoan((response.data?.data ?? {}) as Record<string, unknown>), message: str(response.data?.message) || "Saved." };
}

export async function closeEmployeeLoan(params: { employeeId: string; id: string }): Promise<{ record: EmployeeLoanRecord; message: string }> {
  const response = await api.post<{ message?: string; data?: Record<string, unknown> }>(
    `/api/v1/employees/${encodeURIComponent(params.employeeId)}/loans/${encodeURIComponent(params.id)}/close`,
  );
  return { record: mapLoan((response.data?.data ?? {}) as Record<string, unknown>), message: str(response.data?.message) || "Closed." };
}

export async function deleteEmployeeLoan(params: { employeeId: string; id: string }): Promise<{ message: string }> {
  const response = await api.delete<{ message?: string }>(
    `/api/v1/employees/${encodeURIComponent(params.employeeId)}/loans/${encodeURIComponent(params.id)}`,
  );
  return { message: str(response.data?.message) || "Loan deleted." };
}

export async function listLoanInstallments(params: {
  employeeId: string;
  loanId: string;
  page?: number;
  perPage?: number;
}): Promise<{ rows: EmployeeLoanInstallmentRecord[]; meta: { currentPage: number; lastPage: number; perPage: number; total: number } }> {
  const response = await api.get<PaginatedResponse>(
    `/api/v1/employees/${encodeURIComponent(params.employeeId)}/loans/${encodeURIComponent(params.loanId)}/installments`,
    { params: { page: params.page, per_page: params.perPage } },
  );
  const list = Array.isArray(response.data?.data) ? response.data.data : [];
  const meta = response.data?.meta ?? {};
  return {
    rows: list.map((r) => mapInstallment(r as Record<string, unknown>)),
    meta: {
      currentPage: meta.current_page ?? 1,
      lastPage: meta.last_page ?? 1,
      perPage: meta.per_page ?? params.perPage ?? 20,
      total: meta.total ?? list.length,
    },
  };
}

export async function skipLoanInstallment(params: {
  employeeId: string;
  loanId: string;
  periodYear: number;
  periodMonth: number;
  note?: string | null;
}): Promise<{ record: EmployeeLoanInstallmentRecord; message: string }> {
  const response = await api.post<{ message?: string; data?: Record<string, unknown> }>(
    `/api/v1/employees/${encodeURIComponent(params.employeeId)}/loans/${encodeURIComponent(params.loanId)}/skip-installment`,
    {
      period_year: params.periodYear,
      period_month: params.periodMonth,
      note: params.note?.trim() || null,
    },
  );
  return {
    record: mapInstallment((response.data?.data ?? {}) as Record<string, unknown>),
    message: str(response.data?.message) || "Saved.",
  };
}

export async function recordLoanRepayment(params: {
  employeeId: string;
  loanId: string;
  amount: number;
  paymentDate: string;
  paymentMethod: ExpensePaymentMethod | "";
  bankId: string;
  referenceNo: string;
  chequeNo: string;
  chequeDate: string;
  note?: string | null;
}): Promise<{ record: EmployeeLoanRecord; message: string }> {
  const response = await api.post<{ message?: string; data?: Record<string, unknown> }>(
    `/api/v1/employees/${encodeURIComponent(params.employeeId)}/loans/${encodeURIComponent(params.loanId)}/repay`,
    {
      amount: params.amount,
      payment_date: params.paymentDate,
      note: params.note?.trim() || null,
      ...paymentPayload({
        paymentMethod: params.paymentMethod,
        bankId: params.bankId,
        referenceNo: params.referenceNo,
        chequeNo: params.chequeNo,
        chequeDate: params.chequeDate,
      }),
    },
  );
  return {
    record: mapLoan((response.data?.data ?? {}) as Record<string, unknown>),
    message: str(response.data?.message) || "Repayment recorded.",
  };
}

